import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaFacebook, FaInstagram, FaTwitter, FaYoutube, FaPinterest,
  FaPhone, FaEnvelope, FaMapMarkerAlt, FaGem, FaCreditCard,
  FaShieldAlt, FaTruck, FaCertificate, FaArrowRight
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';

const Footer = () => {
  const { isDarkMode } = useTheme();

  const paymentMethods = [
    'https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg',
    'https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg',
    'https://upload.wikimedia.org/wikipedia/commons/b/b5/PayPal.svg',
    'https://upload.wikimedia.org/wikipedia/commons/f/fa/American_Express_logo_%282018%29.svg'
  ];

  const features = [
    { icon: <FaCertificate />, text: '100% Certified', desc: 'BIS Hallmarked Gold' },
    { icon: <FaTruck />, text: 'Free Shipping', desc: 'On orders above ₹10,000' },
    { icon: <FaShieldAlt />, text: 'Secure Payment', desc: 'SSL Encrypted' },
    { icon: <FaCreditCard />, text: 'Easy Returns', desc: '30 Day Returns' }
  ];

  return (
    <footer style={{
      background: isDarkMode ? 'var(--bg-secondary)' : 'var(--bg-tertiary)',
      marginTop: '80px'
    }}>
      {/* Features Section */}
      <div className="border-bottom" style={{
        borderColor: 'var(--border-color)',
        padding: '40px 0'
      }}>
        <div className="container">
          <div className="row g-4">
            {features.map((feature, index) => (
              <div key={index} className="col-6 col-md-3">
                <motion.div
                  className="text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}>
                  <div className="mb-3" style={{
                    fontSize: '32px',
                    color: 'var(--primary-gold)'
                  }}>
                    {feature.icon}
                  </div>
                  <h6 style={{ fontWeight: '600' }}>{feature.text}</h6>
                  <small className="text-muted">{feature.desc}</small>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="py-5">
        <div className="container">
          <div className="row g-4">
            {/* Company Info */}
            <div className="col-lg-4">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}>
                <div className="d-flex align-items-center mb-4">
                  <FaGem className="me-2" style={{
                    color: 'var(--primary-gold)',
                    fontSize: '32px'
                  }} />
                  <h3 className="gold-text mb-0" style={{
                    fontFamily: 'var(--font-secondary)'
                  }}>
                    Bharat Gold
                  </h3>
                </div>
                <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
                  Experience the finest collection of traditional and modern jewellery.
                  With over 25 years of trust, we bring you certified gold, diamond,
                  and precious stone jewellery.
                </p>
                <div className="d-flex gap-3">
                  {[FaFacebook, FaInstagram, FaTwitter, FaYoutube, FaPinterest].map((Icon, index) => (
                    <motion.a
                      key={index}
                      href="#"
                      whileHover={{ scale: 1.1, y: -3 }}
                      className="text-decoration-none"
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: isDarkMode ? 'var(--bg-tertiary)' : 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--primary-gold)',
                        boxShadow: 'var(--shadow-sm)',
                        transition: 'all 0.3s ease'
                      }}>
                      <Icon />
                    </motion.a>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Quick Links */}
            <div className="col-lg-2 col-md-4 col-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}>
                <h5 className="mb-4" style={{ fontWeight: '600' }}>Quick Links</h5>
                <ul className="list-unstyled">
                  {['About Us', 'Collections', 'New Arrivals', 'Offers', 'Store Locator'].map((link, index) => (
                    <li key={index} className="mb-2">
                      <Link
                        to="/"
                        className="text-decoration-none"
                        style={{
                          color: 'var(--text-secondary)',
                          transition: 'color 0.3s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.color = 'var(--primary-gold)'}
                        onMouseLeave={(e) => e.target.style.color = 'var(--text-secondary)'}>
                        {link}
                      </Link>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* Categories */}
            <div className="col-lg-2 col-md-4 col-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}>
                <h5 className="mb-4" style={{ fontWeight: '600' }}>Categories</h5>
                <ul className="list-unstyled">
                  {['Gold Jewellery', 'Diamond Jewellery', 'Silver Jewellery', 'Platinum', 'Gemstones'].map((category, index) => (
                    <li key={index} className="mb-2">
                      <Link
                        to="/"
                        className="text-decoration-none"
                        style={{
                          color: 'var(--text-secondary)',
                          transition: 'color 0.3s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.color = 'var(--primary-gold)'}
                        onMouseLeave={(e) => e.target.style.color = 'var(--text-secondary)'}>
                        {category}
                      </Link>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* Customer Service */}
            <div className="col-lg-2 col-md-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}>
                <h5 className="mb-4" style={{ fontWeight: '600' }}>Customer Service</h5>
                <ul className="list-unstyled">
                  {['Track Order', 'Return Policy', 'Shipping Info', 'Size Guide', 'Care Instructions'].map((service, index) => (
                    <li key={index} className="mb-2">
                      <Link
                        to="/"
                        className="text-decoration-none"
                        style={{
                          color: 'var(--text-secondary)',
                          transition: 'color 0.3s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.color = 'var(--primary-gold)'}
                        onMouseLeave={(e) => e.target.style.color = 'var(--text-secondary)'}>
                        {service}
                      </Link>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* Contact Info */}
            <div className="col-lg-2 col-md-4">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}>
                <h5 className="mb-4" style={{ fontWeight: '600' }}>Contact Us</h5>
                <ul className="list-unstyled">
                  <li className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaMapMarkerAlt className="me-2 mt-1" style={{ color: 'var(--primary-gold)' }} />
                      <small style={{ color: 'var(--text-secondary)' }}>
                        505 Fazal Imam Complex, Frazer Road, Patna -800001
                      </small>
                    </div>
                  </li>
                  <li className="mb-3">
                    <div className="d-flex align-items-center">
                      <FaPhone className="me-2" style={{ color: 'var(--primary-gold)' }} />
                      <small style={{ color: 'var(--text-secondary)' }}>
                        +91 95255 63933
                      </small>
                    </div>
                  </li>
                  <li className="mb-3">
                    <div className="d-flex align-items-center">
                      <FaEnvelope className="me-2" style={{ color: 'var(--primary-gold)' }} />
                      <small style={{ color: 'var(--text-secondary)' }}>
                        info@bhartigoldbusiness.com
                      </small>
                    </div>
                  </li>
                </ul>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Newsletter Section */}
      <div className="py-4 border-top border-bottom" style={{ borderColor: 'var(--border-color)' }}>
        <div className="container">
          <div className="row align-items-center g-4">
            <div className="col-lg-5">
              <h5 className="mb-0">Subscribe to Our Newsletter</h5>
              <small className="text-muted">Get exclusive offers and new collection updates</small>
            </div>
            <div className="col-lg-7">
              <div className="input-group" style={{ maxWidth: '500px', marginLeft: 'auto' }}>
                <input
                  type="email"
                  className="form-control"
                  placeholder="Enter your email address"
                  style={{
                    borderRadius: '30px 0 0 30px',
                    padding: '12px 20px',
                    border: '1px solid var(--border-color)',
                    background: isDarkMode ? 'var(--bg-primary)' : 'white'
                  }}
                />
                <button
                  className="btn btn-luxury"
                  style={{
                    borderRadius: '0 30px 30px 0',
                    padding: '0 30px'
                  }}>
                  Subscribe <FaArrowRight className="ms-2" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="py-4">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <p className="mb-0 text-muted">
                © 2024 Bharat Gold. All rights reserved. |
                <Link to="/privacy" className="text-decoration-none ms-2" style={{ color: 'var(--primary-gold)' }}>
                  Privacy Policy
                </Link> |
                <Link to="/terms" className="text-decoration-none ms-2" style={{ color: 'var(--primary-gold)' }}>
                  Terms & Conditions
                </Link>
              </p>
            </div>
            <div className="col-lg-6 text-lg-end mt-3 mt-lg-0">
              <div className="d-flex gap-3 justify-content-lg-end align-items-center">
                <small className="text-muted">Secure Payments:</small>
                {paymentMethods.map((method, index) => (
                  <img
                    key={index}
                    src={method}
                    alt="Payment Method"
                    style={{
                      height: '30px',
                      filter: isDarkMode ? 'brightness(0) invert(1)' : 'none'
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;